import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  Kanban as KanbanIcon,
  ClipboardList,
  Upload,
  CalendarClock,
  LogOut,
  BarChart3,
  BookOpen,
  Package,
  Trophy,
  X,
  Linkedin,
} from "lucide-react";

export default function Sidebar({ mobileOpen, onCloseMobile }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "superadmin";
  const [liCount, setLiCount] = useState(0);

  // Poll the LinkedIn count for everyone — SuperAdmin sees unassigned, Members see their allocated.
  useEffect(() => {
    if (!user?.id) return;
    const load = () => api.get("/leads/linkedin-inbox/count").then((r) => setLiCount(r.data?.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [user?.id]);

  const items = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/dashboard/leads", label: "Leads", icon: ClipboardList, testid: "nav-leads" },
    { to: "/dashboard/kanban", label: "Pipeline", icon: KanbanIcon, testid: "nav-kanban" },
    { to: "/dashboard/followups", label: "Follow-ups", icon: CalendarClock, testid: "nav-followups" },
    { to: "/dashboard/upload", label: "Upload Leads", icon: Upload, testid: "nav-upload" },
    { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
    { to: "/dashboard/products", label: "Products", icon: Package, testid: "nav-products" },
    ...(user?.role === "manager" || user?.role === "superadmin"
      ? [{ to: "/dashboard/team", label: "Team Performance", icon: Trophy, testid: "nav-team" }]
      : []),
    { to: "/dashboard/guide", label: "Guide", icon: BookOpen, testid: "nav-guide" },
    ...(isAdmin
      ? [
          { to: "/dashboard/linkedin", label: "LinkedIn", icon: Linkedin, testid: "nav-linkedin", badge: liCount },
          { to: "/dashboard/teams", label: "Teams", icon: Users, testid: "nav-teams" },
          { to: "/dashboard/members", label: "Members", icon: Users, testid: "nav-members" },
        ]
      : [
          { to: "/dashboard/linkedin", label: "LinkedIn", icon: Linkedin, testid: "nav-linkedin", badge: liCount },
        ]),
  ];

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={onCloseMobile} data-testid="sidebar-backdrop" />
      )}
      <aside
        className={`
          fixed left-0 top-12 bottom-0 w-56 bg-white border-r border-slate-200 flex flex-col z-40
          transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
        data-testid="sidebar"
      >
        <div className="md:hidden flex justify-end p-2">
          <button onClick={onCloseMobile} className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center" data-testid="sidebar-close">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              data-testid={it.testid}
              onClick={onCloseMobile}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
            >
              <it.icon className="w-4 h-4" strokeWidth={2} />
              <span className="flex-1">{it.label}</span>
              {it.badge > 0 && (
                <span className="min-w-[18px] h-4 px-1 rounded-full bg-[#0A66C2] text-white text-[10px] font-semibold flex items-center justify-center" data-testid={`${it.testid}-badge`}>
                  {it.badge > 99 ? "99+" : it.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-2 mb-2">
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Signed in as</p>
            <p className="text-[13px] font-medium truncate text-slate-800" data-testid="current-user-name">{user?.name}</p>
            <p className="text-[10px] text-[#1D63ED] font-medium uppercase tracking-wider">{user?.role}</p>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="nav-item w-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
}
